/**
 * DEMO ONLY — dynamic pricing slabs concept.
 *
 * Pure quantity -> band -> unit price resolution. Mirrors the planned backend
 * `PriceHelper::resolveUnitPrice()` described in
 * price_book_update/plans_and_actions/07-design-dynamic-slabs.md §4.
 *
 * A "band" is { minQty: number, maxQty: number|null, label: string }.
 * maxQty === null means the open-ended top band.
 */

export const OUTSIDE_BANDS = "OUTSIDE_BANDS";
export const NO_PRICE_SET = "NO_PRICE_SET";

/**
 * Find the band a quantity falls into. Returns the band, or null if the
 * quantity is below the floor or lands in a gap between bands.
 */
export function resolveBand(bands, qty) {
  if (!Array.isArray(bands) || bands.length === 0) return null;
  const ordered = [...bands].sort((a, b) => a.minQty - b.minQty);
  return (
    ordered.find(
      (b) => qty >= b.minQty && (b.maxQty === null || qty <= b.maxQty)
    ) || null
  );
}

/**
 * Resolve a unit price (in cents) for a product at a quantity.
 *
 * product: {
 *   bands: band[],                       // the resolved set (global or custom)
 *   valuesCents: { [bandLabel]: number } // price per band, in cents
 * }
 *
 * Returns { ok: true, band, unitPriceCents } or
 *         { ok: false, reason: OUTSIDE_BANDS | NO_PRICE_SET, band? , message }.
 */
export function resolvePrice(product, qty) {
  const q = Number(qty);
  if (!Number.isFinite(q) || q <= 0) {
    return {
      ok: false,
      reason: OUTSIDE_BANDS,
      message: `Enter a quantity of 1 or more.`,
    };
  }

  const band = resolveBand(product.bands, q);
  if (!band) {
    const floor = Math.min(...product.bands.map((b) => b.minQty));
    return {
      ok: false,
      reason: OUTSIDE_BANDS,
      message:
        q < floor
          ? `Quantity ${q} is below the minimum order of ${floor} for this item.`
          : `Quantity ${q} is outside the price bands configured for this item.`,
    };
  }

  const value = product.valuesCents ? product.valuesCents[band.label] : undefined;
  if (value === undefined || value === null || value === "") {
    return {
      ok: false,
      reason: NO_PRICE_SET,
      band,
      message: `No price is set for the "${band.label}" band on this item.`,
    };
  }

  return { ok: true, band, unitPriceCents: Number(value) };
}

/** Format an integer-cents value as a dollar string. */
export function formatCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return "-";
  return `$${(n / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
